import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Tap "Sign Up" on the login screen and enter your email address and a password. You can also use Sign In with Apple for a faster setup. You will receive a verification code via email to confirm your account.',
  },
  {
    question: 'I forgot my password. How do I reset it?',
    answer:
      'On the login screen, tap "Forgot Password" and enter your email address. You will receive a reset code via email. Enter the code and choose a new password.',
  },
  {
    question: 'How do I browse recipes?',
    answer:
      'Use the Browse tab to see all 148+ recipes. You can filter by cuisine, region, difficulty level, dietary requirements (over 40 tags including vegetarian, gluten-free, dairy-free, and more), and age group.',
  },
  {
    question: 'How do I save a recipe to my cookbook?',
    answer:
      'Open any recipe and tap the heart/save icon. The recipe will appear in the My Cookbook tab under "Saved". Once you have cooked a recipe, it moves to the "Cooked" section.',
  },
  {
    question: 'What age groups are supported?',
    answer:
      'Recipes are tagged for suitability across age groups: Baby (6-12 months), Toddler (1-3 years), Child (4-12 years), Teen (13-17 years), and Adult (18+). Use the age group filter to find recipes appropriate for your family.',
  },
  {
    question: 'Can I see nutrition information?',
    answer:
      'Yes. Each recipe includes full nutrition data (calories, protein, carbs, fat, fibre, sugar, sodium, and more). Open a recipe and tap the "Nutrition" tab to see the breakdown per serving with percentage of daily values.',
  },
  {
    question: 'How do I switch between imperial and metric measurements?',
    answer:
      'On any recipe detail page, use the US/Metric toggle to switch between imperial (cups, oz, Fahrenheit) and metric (ml, g, Celsius) measurements. Your preference is saved automatically and applies across all recipes.',
  },
  {
    question: 'How do I plan my meals?',
    answer:
      'Go to the Meal Plan tab and select a week. Tap on any day to assign recipes to breakfast, lunch, dinner, or snacks. You can drag recipes between days to rearrange your plan.',
  },
  {
    question: 'How do I generate a grocery list?',
    answer:
      'From your meal plan, tap "Generate Grocery List" to automatically create a shopping list from all the recipes in your plan. Ingredients are combined and grouped by category (produce, dairy, meat, pantry, etc.). You can add custom items, remove items, and check off items as you shop.',
  },
  {
    question: 'Do grocery lists work offline?',
    answer:
      'Yes. Your grocery list is stored locally on your device and works without an internet connection. It will sync to the cloud when you reconnect.',
  },
  {
    question: 'What is cooking mode?',
    answer:
      'Cooking mode provides a step-by-step guided experience for following a recipe. It displays one instruction at a time in a large, easy-to-read format designed for use in the kitchen. Swipe or tap to move between steps. The screen stays awake during cooking mode so you don\'t have to keep unlocking your device.',
  },
  {
    question: 'Does Cooksmith work offline?',
    answer:
      'Yes. Cooksmith is built as a Progressive Web App (PWA) with offline support. All your saved recipes, meal plans, and grocery lists are stored locally on your device. You can browse your cookbook, follow recipes, and use grocery lists without an internet connection. Changes sync automatically when you reconnect.',
  },
  {
    question: 'What do I get with the Free plan?',
    answer:
      'The Free plan includes: browse all 148+ recipes, save up to 15 favourites, create up to 12 of your own recipes, 1 grocery list, 1 cooking session per week, and imperial/metric unit toggle.',
  },
  {
    question: 'What does Premium include?',
    answer:
      'Premium (£4.99/month or £39.99/year) unlocks: unlimited saved recipes, unlimited recipe creation, unlimited grocery lists, unlimited cooking sessions, multi-week meal planning, and priority support.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'On iOS: open Settings on your iPhone, tap your name, tap Subscriptions, find Cooksmith, and tap Cancel Subscription. On the web: go to your Profile settings, tap Manage Subscription, and cancel via the Stripe customer portal. Your Premium access remains active until the end of your billing period. After cancellation, your account reverts to the Free plan and your saved data is not deleted.',
  },
  {
    question: 'I see different data on two devices. What should I do?',
    answer:
      'Ensure both devices are connected to the internet and open the app to trigger a sync. If the issue persists, try signing out and signing back in. If data is still inconsistent, contact us at support@wolftechnologies.co.uk.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const CooksmithSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Cooksmith Support</h1>
          <p className="legal-page__subtitle">
            Get help with Cooksmith by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all enquiries within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Cooksmith</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/cooksmith/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CooksmithSupport;
